import React, { useMemo } from 'react';
import type { PassportCard } from '../../services/api/passportCards';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import PassportRevealShowcase, {
  type PassportRevealSample,
} from '../passport/PassportRevealShowcase';

function interfaceSample(input: {
  type: 'shop' | 'club' | 'event';
  title: string;
  subtitle: string;
  image: string;
  story: string;
}): PassportCard {
  return {
    id: null,
    title: input.title,
    issuer: 'Bilt:Spec preview',
    subject_type: input.type,
    subject_id: 0,
    status: 'preview',
    rules: { evidence: 'qr', cap: null },
    design: {
      template: input.type === 'shop' ? 'place' : input.type,
      finish: 'silver',
      accent: input.type === 'event' ? 'violet' : 'orange',
      layout: 'portrait',
      story: input.story,
      crop: 50,
      art_id: '',
    },
    subject: {
      subject_type: input.type,
      subject_id: 0,
      title: input.title,
      subtitle: input.subtitle,
      image_url: input.image,
      collectible: {
        kind: input.type,
        subtitle: input.subtitle,
        image_url: input.image,
        cover_url: input.image,
      },
    },
  };
}

export default function HomePassportReveal({ buildCard }: { buildCard: PassportCard }) {
  const samples = useMemo<PassportRevealSample[]>(
    () => [
      {
        id: 'vehicle',
        label: 'Build',
        title: 'The build becomes the object.',
        detail:
          'The full collector card keeps the car recognizable. Turn it over and the build record explains what is known without estimating what is not.',
        action: 'View build',
        resultDetail: 'Selected public build · sample reveal',
        backgroundImage:
          resolvePublicFileUrl(buildCard.subject?.image_url) || '/images/brand/home-build-record-wide.jpg',
        card: buildCard,
      },
      {
        id: 'shop',
        label: 'Shop',
        title: 'A place can carry its own record.',
        detail:
          'Shop cards use the same physical system, with public place context on the reverse. A collection record is not a rating or purchase receipt.',
        action: 'View shop',
        resultDetail: 'Shop card · interface preview',
        backgroundImage: '/images/brand/home-story-workshop.jpg',
        card: interfaceSample({
          type: 'shop',
          title: 'A shop worth finding again',
          subtitle: 'Public location details appear when supplied',
          image: '/images/brand/home-story-workshop.jpg',
          story:
            'A sample shop-card layout. A live card uses the shop’s supplied public details and collection rules.',
        }),
      },
      {
        id: 'event',
        label: 'Event',
        title: 'A gathering becomes a chapter.',
        detail:
          'Event cards can preserve published context and issuer story. Collecting one does not replace admission or prove attendance.',
        action: 'View event',
        resultDetail: 'Event card · interface preview',
        backgroundImage: '/images/brand/home-event-chapter-wide.jpg',
        card: interfaceSample({
          type: 'event',
          title: 'The next gathering',
          subtitle: 'Published event details appear when supplied',
          image: '/images/brand/home-event-chapter-wide.jpg',
          story:
            'A sample event-card layout. Live event cards use the issuer’s artwork, story and verified collection rules.',
        }),
      },
      {
        id: 'club',
        label: 'Club',
        title: 'The people you found stay connected.',
        detail:
          'Club cards carry public community context without pretending that a scan is membership, verification or endorsement.',
        action: 'View club',
        resultDetail: 'Club card · interface preview',
        backgroundImage: '/images/brand/home-club-community.jpg',
        card: interfaceSample({
          type: 'club',
          title: 'A community to return to',
          subtitle: 'Public club details appear when supplied',
          image: '/images/brand/home-club-community.jpg',
          story:
            'A sample club-card layout. A live card uses the club’s supplied identity and does not act as a membership credential.',
        }),
      },
    ],
    [buildCard]
  );

  return (
    <PassportRevealShowcase
      id="passport-reveal"
      className="bh-scan-showcase bh-section"
      eyebrow="Inside a real scan"
      heading={
        <>
          One scan.
          <br />
          <span>The whole card.</span>
        </>
      }
      intro="A build is only one kind of discovery. Shops, events and clubs use the same full collector-card system, each with a reverse that explains its real record."
      samples={samples}
      disclosure={
        <p>
          Interactive production-card preview. The selected build uses current public content when
          available. Shop, event and club examples demonstrate the interface only. Nothing here
          records a scan, issues a card, proves attendance or joins a club.
        </p>
      }
    />
  );
}
